import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type ServerConfig } from '../shared/api.ts';
import { formatBytes, formatClock, formatRate } from '../shared/format.ts';
import { filesFromDataTransfer, filesFromInput, type PickedFile } from './files.ts';
import { totalsOf, Uploader, type UploadItem } from './uploader.ts';
import { useTurnstile } from './turnstile.ts';
import './drop.css';

interface Session {
  sessionId: string;
  code: string;
  expiresAt: number;
  startedAt: number;
}

interface PushedItem {
  token: string;
  name: string;
  size: number;
  count: number;
  url: string;
}

type Phase = 'idle' | 'live' | 'hold' | 'drain' | 'scar';
type StageStatus = 'boot' | 'ready' | 'expired' | 'error';

const CLOCK_THRESHOLD_MS = 60_000;
const COPIED_MS = 900;
const HOLD_MS = 720;
const DRAIN_MS = 880;
const MARK_MS = 1900;
const ORB_DOTS = 108;

interface OrbTarget {
  x: number;
  y: number;
  active: boolean;
  kick: number;
}

/**
 * The public transfer surface. The dotted orb never changes identity: it is
 * the resting target, the drag response, the progress meter, and the success
 * object. The receive panel moves the same object aside instead of replacing
 * the screen, which keeps every state spatially connected.
 */
export function DropPage(): React.JSX.Element {
  const [config, setConfig] = useState<ServerConfig | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [pushed, setPushed] = useState<PushedItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [copied, setCopied] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [mark, setMark] = useState(0);

  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const uploader = useRef<Uploader | null>(null);
  const dragDepth = useRef(0);
  const orb = useRef<HTMLDivElement>(null);
  const orbTarget = useRef<OrbTarget>({ x: 0, y: 0, active: false, kick: 0 });
  const wakeOrb = useRef<() => void>(() => {});

  const { widgetRef, token: turnstileToken, reset: resetTurnstile } = useTurnstile(
    config?.turnstileRequired ? config.turnstileSiteKey : null,
  );

  useEffect(() => {
    api.config().then(setConfig).catch(() => setError('Cannot reach the server.'));
  }, []);

  useEffect(() => {
    if (!config || session || expired) return;
    if (config.turnstileRequired && !turnstileToken) return;

    let cancelled = false;
    api
      .createSession(turnstileToken ?? undefined)
      .then((next) => {
        if (!cancelled) setSession({ ...next, startedAt: Date.now() });
      })
      .catch((reason: Error) => {
        if (cancelled) return;
        setError(reason.message);
        resetTurnstile();
      });
    return () => {
      cancelled = true;
    };
  }, [config, session, expired, turnstileToken, resetTurnstile]);

  useEffect(() => {
    if (!session || !config) return;
    uploader.current = new Uploader({
      endpoint: '/api/upload',
      chunkSize: config.chunkSize,
      sessionId: session.sessionId,
      onChange: setItems,
    });
    return () => {
      uploader.current?.abortAll();
      uploader.current = null;
    };
  }, [session, config]);

  useEffect(() => {
    if (!session) return;
    const source = new EventSource(`/api/session/${session.code}/events`);
    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as
          | { type: 'ready' }
          | { type: 'pushed'; item: PushedItem }
          | { type: 'expired' };
        if (data.type === 'pushed') {
          setPushed((previous) =>
            previous.some((item) => item.token === data.item.token)
              ? previous
              : [data.item, ...previous],
          );
          setPanelOpen(true);
        }
        if (data.type === 'expired') {
          setSession(null);
          setExpired(true);
        }
      } catch {
        // Ignore a malformed event frame and leave the live stream connected.
      }
    };
    return () => source.close();
  }, [session]);

  useEffect(() => {
    if (!session) return;
    const tick = (): void => {
      const left = session.expiresAt - Date.now();
      setRemaining(left);
      if (left <= 0) {
        setSession(null);
        setExpired(true);
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [session]);

  const accept = useCallback((picked: PickedFile[]) => {
    if (picked.length === 0) return;
    uploader.current?.add(picked);
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      orbTarget.current = {
        x: 0,
        y: 0,
        active: false,
        kick: orbTarget.current.kick + 1,
      };
      wakeOrb.current();
      void filesFromDataTransfer(event.dataTransfer).then(accept);
    },
    [accept],
  );

  // The drag response is integrated per frame. A leave or drop therefore
  // retargets from the current presentation value instead of visibly jumping.
  useEffect(() => {
    const element = orb.current;
    if (!element) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const model = { x: 0, y: 0, vx: 0, vy: 0, sx: 1, sy: 1, vsx: 0, vsy: 0, r: 0, vr: 0 };
    let frame = 0;
    let previous = 0;
    let seenKick = orbTarget.current.kick;

    const spring = (value: number, velocity: number, target: number, dt: number): [number, number] => {
      const acceleration = 155 * (target - value) - 24 * velocity;
      const nextVelocity = velocity + acceleration * dt;
      return [value + nextVelocity * dt, nextVelocity];
    };

    const tick = (now: number): void => {
      const dt = Math.min(previous === 0 ? 1 / 60 : (now - previous) / 1000, 0.034);
      previous = now;
      const target = orbTarget.current;
      const targetScaleX = target.active ? 1.026 + Math.abs(target.x) * 0.0014 : 1;
      const targetScaleY = target.active ? 1.012 + Math.abs(target.y) * 0.001 : 1;

      if (seenKick !== target.kick) {
        seenKick = target.kick;
        model.vsx -= 0.72;
        model.vsy -= 0.72;
      }

      if (reduced.matches) {
        model.x = 0;
        model.y = 0;
        model.sx = 1;
        model.sy = 1;
        model.r = 0;
      } else {
        [model.x, model.vx] = spring(model.x, model.vx, target.x, dt);
        [model.y, model.vy] = spring(model.y, model.vy, target.y, dt);
        [model.sx, model.vsx] = spring(model.sx, model.vsx, targetScaleX, dt);
        [model.sy, model.vsy] = spring(model.sy, model.vsy, targetScaleY, dt);
        [model.r, model.vr] = spring(model.r, model.vr, target.active ? target.x * 0.075 : 0, dt);
      }

      element.style.transform = `translate3d(${model.x.toFixed(2)}px, ${model.y.toFixed(2)}px, 0) rotate(${model.r.toFixed(2)}deg) scale(${model.sx.toFixed(4)}, ${model.sy.toFixed(4)})`;
      const moving =
        target.active ||
        Math.abs(target.x) + Math.abs(target.y) > 0.01 ||
        Math.abs(model.x) + Math.abs(model.y) > 0.05 ||
        Math.abs(model.sx - 1) + Math.abs(model.sy - 1) > 0.0005 ||
        Math.abs(model.vx) + Math.abs(model.vy) + Math.abs(model.vsx) + Math.abs(model.vsy) > 0.02;
      if (moving) frame = requestAnimationFrame(tick);
      else {
        frame = 0;
        previous = 0;
        element.style.transform = '';
      }
    };

    wakeOrb.current = (): void => {
      if (frame === 0) frame = requestAnimationFrame(tick);
    };
    return () => {
      wakeOrb.current = () => {};
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, []);

  const totals = useMemo(() => totalsOf(items), [items]);
  const rate = useRate(totals.sent, totals.active);
  const totalsRef = useRef(totals);
  totalsRef.current = totals;

  useEffect(() => {
    if (totals.files === 0) setPhase('idle');
    else if (totals.active) setPhase('live');
    else setPhase((current) => (current === 'live' ? 'hold' : current));
  }, [totals.files, totals.active]);

  useEffect(() => {
    if (phase !== 'hold') return;
    const timer = setTimeout(
      () => setPhase(totalsRef.current.failed > 0 ? 'scar' : 'drain'),
      HOLD_MS,
    );
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'drain') return;
    const timer = setTimeout(() => uploader.current?.clear(), DRAIN_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'hold' || totalsRef.current.failed > 0) return;
    setMark((current) => current + 1);
    const timer = setTimeout(() => setMark(0), MARK_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const progress = totals.bytes > 0 ? Math.min(1, totals.sent / totals.bytes) : 1;
  const faults = items.filter((item) => item.state === 'error');
  const status: StageStatus = error ? 'error' : expired ? 'expired' : session ? 'ready' : 'boot';

  const copyCode = useCallback(() => {
    if (!session) return;
    void navigator.clipboard?.writeText(session.code).then(() => setCopied(true));
  }, [session]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const onShellClick = useCallback(
    (event: React.MouseEvent) => {
      if (!window.getSelection()?.isCollapsed) return;
      if ((event.target as HTMLElement).closest('[data-interactive]')) return;
      if (expired) {
        setExpired(false);
        setError(null);
        resetTurnstile();
        return;
      }
      fileInput.current?.click();
    },
    [expired, resetTurnstile],
  );

  const readout = totals.files > 0
    ? `${formatBytes(totals.sent)} / ${formatBytes(totals.bytes)}${
        totals.active ? ` · ${formatRate(rate)}` : ''
      } · ${totals.done}/${totals.files}`
    : '';

  return (
    <main
      className={`drop${dragging ? ' is-dragging' : ''}${panelOpen ? ' is-panel-open' : ''}`}
      data-phase={phase}
      data-status={status}
      onClick={onShellClick}
      onDragEnter={(event) => {
        event.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
        orbTarget.current.active = true;
        wakeOrb.current();
      }}
      onDragOver={(event) => {
        event.preventDefault();
        const bounds = orb.current?.getBoundingClientRect();
        if (!bounds) return;
        const nx = Math.max(-1, Math.min(1, (event.clientX - bounds.left - bounds.width / 2) / (bounds.width / 2)));
        const ny = Math.max(-1, Math.min(1, (event.clientY - bounds.top - bounds.height / 2) / (bounds.height / 2)));
        orbTarget.current.x = nx * 15;
        orbTarget.current.y = ny * 15;
        orbTarget.current.active = true;
        wakeOrb.current();
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        dragDepth.current = event.relatedTarget === null ? 0 : dragDepth.current - 1;
        if (dragDepth.current <= 0) {
          setDragging(false);
          orbTarget.current = { ...orbTarget.current, x: 0, y: 0, active: false };
          wakeOrb.current();
        }
      }}
      onDrop={onDrop}
    >
      <SessionCode
        status={status}
        words={session?.code.split('-') ?? []}
        errorText={error}
        copied={copied}
        remaining={remaining}
        onCopy={copyCode}
      />

      <button
        type="button"
        className="receive-toggle"
        aria-label={panelOpen ? 'Close received files' : 'Open received files'}
        aria-expanded={panelOpen}
        aria-controls="receive-panel"
        onClick={() => setPanelOpen((open) => !open)}
        data-has-files={pushed.length > 0}
        data-interactive
      >
        <DownloadIcon />
      </button>

      <div className="orb-positioner">
        <div className="orb-reactor" ref={orb}>
          <DottedOrb progress={progress} active={totals.files > 0} failed={faults.length > 0} />
          <div className="orb-content" aria-live="polite">
            {dragging ? <p className="orb-prompt">release</p> : null}
            {!dragging && readout ? (
              <p className="orb-readout mono" data-suppressed={mark > 0}>
                {readout}
              </p>
            ) : null}
            {!dragging && mark > 0 ? (
              <svg className="orb-mark" key={mark} viewBox="0 0 100 100" aria-hidden="true">
                <path d="M 22 52 L 42 71 L 79 30" />
              </svg>
            ) : null}
            {!dragging && faults.length > 0 ? (
              <ul className="orb-faults mono">
                {faults.map((fault) => <li key={fault.id}>{fault.name} — {fault.error}</li>)}
              </ul>
            ) : null}
            {!dragging && status === 'expired' && totals.files === 0 ? (
              <p className="orb-prompt">click to renew</p>
            ) : null}
          </div>
        </div>
        <div
          ref={widgetRef}
          className="drop__turnstile"
          data-verified={turnstileToken !== null || session !== null}
          data-interactive
        />
      </div>

      <ReceivePanel
        open={panelOpen}
        items={pushed}
        onRemove={(token) => setPushed((current) => current.filter((item) => item.token !== token))}
      />

      <button type="button" className="folder-pick" onClick={() => folderInput.current?.click()} data-interactive>
        folder
      </button>

      <input
        ref={fileInput}
        type="file"
        multiple
        className="visually-hidden"
        onChange={(event) => {
          accept(filesFromInput(event.target.files));
          event.target.value = '';
        }}
      />
      <input
        ref={folderInput}
        type="file"
        multiple
        className="visually-hidden"
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        onChange={(event) => {
          accept(filesFromInput(event.target.files));
          event.target.value = '';
        }}
      />
    </main>
  );
}

interface SessionCodeProps {
  status: StageStatus;
  words: string[];
  errorText: string | null;
  copied: boolean;
  remaining: number;
  onCopy: () => void;
}

function SessionCode(props: SessionCodeProps): React.JSX.Element {
  return (
    <div className="session-code" data-status={props.status} data-copied={props.copied}>
      {props.status === 'ready' ? (
        <button type="button" className="session-code__words" onClick={props.onCopy} data-interactive>
          {props.words.map((word) => <span key={word}>{word}</span>)}
          <span className="visually-hidden">Copy device code</span>
        </button>
      ) : props.status === 'error' ? (
        <p className="session-code__message">{props.errorText}</p>
      ) : props.status === 'expired' ? (
        <p className="session-code__message">session expired</p>
      ) : (
        <div className="session-code__loading" aria-label="Creating a device code">
          <i /><i /><i />
        </div>
      )}
      {props.status === 'ready' && props.remaining <= CLOCK_THRESHOLD_MS ? (
        <span className="session-code__clock mono">{formatClock(props.remaining)}</span>
      ) : null}
    </div>
  );
}

interface DottedOrbProps {
  progress: number;
  active: boolean;
  failed: boolean;
}

function DottedOrb({ progress, active, failed }: DottedOrbProps): React.JSX.Element {
  const completeDots = active ? Math.floor(progress * ORB_DOTS) : ORB_DOTS;
  const dots = useMemo(
    () => Array.from({ length: ORB_DOTS }, (_, index) => {
      const angle = (index / ORB_DOTS) * Math.PI * 2 - Math.PI / 2;
      return {
        index,
        x: 50 + Math.cos(angle) * 47.5,
        y: 50 + Math.sin(angle) * 47.5,
        radius: 0.3 + 0.055 * (0.5 + 0.5 * Math.sin(index * 1.73)),
      };
    }),
    [],
  );

  return (
    <svg className="orb-ring" data-active={active} data-failed={failed} viewBox="0 0 100 100" aria-hidden="true">
      <g className="orb-ring__rotor">
        {dots.map((dot) => (
          <circle
            className={dot.index < completeDots ? 'is-complete' : ''}
            key={dot.index}
            cx={dot.x}
            cy={dot.y}
            r={dot.radius}
          />
        ))}
      </g>
    </svg>
  );
}

interface ReceivePanelProps {
  open: boolean;
  items: PushedItem[];
  onRemove: (token: string) => void;
}

function ReceivePanel({ open, items, onRemove }: ReceivePanelProps): React.JSX.Element {
  const [removing, setRemoving] = useState<string[]>([]);
  const removalTimers = useRef<number[]>([]);

  useEffect(() => () => {
    removalTimers.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const remove = (token: string): void => {
    if (removing.includes(token)) return;
    setRemoving((current) => [...current, token]);
    const timer = window.setTimeout(() => {
      onRemove(token);
      setRemoving((current) => current.filter((item) => item !== token));
    }, 340);
    removalTimers.current.push(timer);
  };

  return (
    <aside
      className="receive-panel"
      id="receive-panel"
      data-interactive
      {...(!open ? { inert: true, 'aria-hidden': true } : {})}
    >
      <div className="receive-panel__header">
        <span>receive</span>
        <span className="mono">{items.length || '—'}</span>
      </div>
      {items.length === 0 ? (
        <p className="receive-panel__empty">received files appear here</p>
      ) : (
        <ul className="receive-list" aria-live="polite">
          {items.map((item) => (
            <li className="receive-file" key={item.token} data-removing={removing.includes(item.token)}>
              <a className="receive-file__download" href={item.url} download data-interactive>
                <span className="receive-file__details">
                  <span className="receive-file__name">{item.name}</span>
                  <span className="receive-file__size mono">
                    {item.count > 1 ? `${item.count} files · ` : ''}{formatBytes(item.size)}
                  </span>
                </span>
                <span className="receive-file__action">download</span>
              </a>
              <button
                type="button"
                className="receive-file__remove"
                aria-label={`Remove ${item.name} from the list`}
                onClick={() => remove(item.token)}
                data-interactive
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function DownloadIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6.8v9.1M8.6 12.8 12 16.2l3.4-3.4" />
    </svg>
  );
}

/** Smoothed transfer rate. A raw derivative jitters too much to read. */
function useRate(sent: number, active: boolean): number {
  const [rate, setRate] = useState(0);
  const samples = useRef<{ at: number; sent: number }[]>([]);

  useEffect(() => {
    if (!active) {
      samples.current = [];
      setRate(0);
      return;
    }
    const timer = setInterval(() => {
      const now = Date.now();
      samples.current.push({ at: now, sent });
      samples.current = samples.current.filter((sample) => now - sample.at <= 5000);
      const first = samples.current[0];
      const last = samples.current.at(-1);
      if (first && last && last.at > first.at) {
        setRate(((last.sent - first.sent) * 1000) / (last.at - first.at));
      }
    }, 500);
    return () => clearInterval(timer);
  }, [sent, active]);

  return rate;
}
